import { Component } from '../Component';

/**
 * Status effect types
 */
export type StatusEffectType =
  | 'poison'     // Damage over time
  | 'burn'       // Damage over time + attack reduction
  | 'freeze'     // Cannot act
  | 'paralysis'  // Reduced speed, chance to skip turn
  | 'sleep'      // Cannot act, wakes on damage
  | 'confusion'  // May hurt self
  | 'regen'      // Heal over time
  | 'shield'     // Damage reduction
  | 'haste'      // Increased speed
  | 'strength'   // Increased attack
  | 'weakness';  // Reduced attack

/**
 * Active status effect on an entity
 */
export interface StatusEffect {
  type: StatusEffectType;
  duration: number;      // Remaining duration in ms
  strength: number;      // Effect magnitude
  source?: string;       // Entity ID that applied it
}

/**
 * HealthComponent - Health and combat state
 */
export class HealthComponent extends Component {
  public readonly type = 'health';

  /** Current health points */
  public current: number;

  /** Maximum health points */
  public max: number;

  /** Current energy (for abilities) */
  public energy: number;

  /** Maximum energy */
  public maxEnergy: number;

  /** Active status effects */
  public statusEffects: StatusEffect[];

  /** Whether entity is alive */
  public isAlive: boolean;

  /** Whether entity is fainted (for party members - alive but incapacitated) */
  public isFainted: boolean;

  /** Invulnerability timer (for damage immunity frames) */
  public invulnerableUntil: number;

  /** Last damage taken (for UI feedback) */
  public lastDamageTaken: number;

  /** Last heal received */
  public lastHealReceived: number;

  constructor(data?: Partial<HealthComponent>) {
    super();
    this.current = data?.current ?? data?.max ?? 100;
    this.max = data?.max ?? 100;
    this.energy = data?.energy ?? data?.maxEnergy ?? 50;
    this.maxEnergy = data?.maxEnergy ?? 50;
    this.statusEffects = data?.statusEffects ?? [];
    this.isAlive = data?.isAlive ?? true;
    this.isFainted = data?.isFainted ?? false;
    this.invulnerableUntil = data?.invulnerableUntil ?? 0;
    this.lastDamageTaken = 0;
    this.lastHealReceived = 0;
  }

  /**
   * Get health as percentage (0-1)
   */
  public get healthPercent(): number {
    return this.current / this.max;
  }

  /**
   * Get energy as percentage (0-1)
   */
  public get energyPercent(): number {
    return this.energy / this.maxEnergy;
  }

  /**
   * Take damage
   * @returns Actual damage taken after modifiers
   */
  public takeDamage(amount: number, currentTime: number = 0): number {
    // Check invulnerability
    if (currentTime < this.invulnerableUntil) {
      return 0;
    }

    // Apply damage reduction from shield status
    const shieldEffect = this.statusEffects.find((e) => e.type === 'shield');
    if (shieldEffect) {
      amount = Math.floor(amount * (1 - shieldEffect.strength));
    }

    const actualDamage = Math.min(amount, this.current);
    this.current -= actualDamage;
    this.lastDamageTaken = actualDamage;

    if (this.current <= 0) {
      this.current = 0;
      this.isAlive = false;
    }

    return actualDamage;
  }

  /**
   * Heal health
   * @returns Actual amount healed
   */
  public heal(amount: number): number {
    const actualHeal = Math.min(amount, this.max - this.current);
    this.current += actualHeal;
    this.lastHealReceived = actualHeal;
    return actualHeal;
  }

  /**
   * Restore energy
   * @returns Actual amount restored
   */
  public restoreEnergy(amount: number): number {
    const actualRestore = Math.min(amount, this.maxEnergy - this.energy);
    this.energy += actualRestore;
    return actualRestore;
  }

  /**
   * Spend energy
   * @returns Whether there was enough energy
   */
  public spendEnergy(amount: number): boolean {
    if (this.energy < amount) {
      return false;
    }
    this.energy -= amount;
    return true;
  }

  /**
   * Add a status effect
   */
  public addStatusEffect(effect: StatusEffect): void {
    // Check if effect already exists
    const existing = this.statusEffects.find((e) => e.type === effect.type);
    if (existing) {
      // Refresh duration and take higher strength
      existing.duration = Math.max(existing.duration, effect.duration);
      existing.strength = Math.max(existing.strength, effect.strength);
    } else {
      this.statusEffects.push({ ...effect });
    }
  }

  /**
   * Remove a status effect
   */
  public removeStatusEffect(type: StatusEffectType): void {
    this.statusEffects = this.statusEffects.filter((e) => e.type !== type);
  }

  /**
   * Check if entity has a status effect
   */
  public hasStatusEffect(type: StatusEffectType): boolean {
    return this.statusEffects.some((e) => e.type === type);
  }

  /**
   * Update status effect durations
   */
  public updateStatusEffects(delta: number): void {
    this.statusEffects = this.statusEffects.filter((effect) => {
      effect.duration -= delta;
      return effect.duration > 0;
    });
  }

  /**
   * Revive with specified health percentage
   */
  public revive(healthPercent: number = 0.5): void {
    this.current = Math.floor(this.max * healthPercent);
    this.isAlive = true;
    this.isFainted = false;
    this.statusEffects = [];
  }

  /**
   * Set entity to fainted state (for party members)
   */
  public faint(): void {
    this.current = 0;
    this.isFainted = true;
    this.statusEffects = [];
  }
}
